import { useLayoutEffect, useRef, useState } from 'react';

type ScrollEdges = {
  canScrollUp: boolean;
  canScrollDown: boolean;
};

const NO_EDGES: ScrollEdges = { canScrollUp: false, canScrollDown: false };

/**
 * Whether a vertical scroller has more content above / below the visible part.
 * Attach `scrollerRef` to the scroll container. Re-checks on scroll and whenever the scroller
 * or its children change size (e.g. a collapse menu inside the list opens). Children are
 * observed once on mount.
 */
export const useScrollEdges = () => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState(NO_EDGES);

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;

    if (!scroller) {
      return undefined;
    }

    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = scroller;
      const canScrollUp = scrollTop > 0;
      // -1: fractional scrollTop never reaches scrollHeight - clientHeight exactly
      const canScrollDown = scrollTop + clientHeight < scrollHeight - 1;

      setEdges((prev) =>
        prev.canScrollUp === canScrollUp && prev.canScrollDown === canScrollDown
          ? prev
          : { canScrollUp, canScrollDown },
      );
    };

    // the scroller's own box is fixed by the flex layout; content growth is a child resize
    const resizeObserver = new ResizeObserver(update);

    update();
    scroller.addEventListener('scroll', update, { passive: true });
    resizeObserver.observe(scroller);
    Array.from(scroller.children).forEach((child) => resizeObserver.observe(child));

    return () => {
      scroller.removeEventListener('scroll', update);
      resizeObserver.disconnect();
    };
  }, []);

  return { scrollerRef, ...edges };
};
